import { Injectable, inject } from '@angular/core';
import { ClientLoggingService } from './client-logging.service';
import { PlanReviewerService } from './plan-reviewer.service';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';

/**
 * Issue #1172: Prefetches compliance report and PDF data for the active page
 * (sibling-tab preloading) and for adjacent pages in the TOC (neighbor-page
 * preloading). All fetches are fire-and-forget — errors are logged as
 * warnings so preloading never blocks the critical path.
 */
@Injectable({ providedIn: 'root' })
export class PagePreloadService {
  private planReviewerService = inject(PlanReviewerService);

  private static readonly MAX_NEIGHBORS = 1;

  /**
   * Warm the compliance-report and PDF byte caches for a page.
   * Both service methods already deduplicate in-flight requests via their
   * internal Promise/Map caches, so calling this is always safe.
   */
  preloadPageData(page: ArchitecturalPlanPage): void {
    this.planReviewerService
      .getPageComplianceReport(page)
      .catch(err =>
        ClientLoggingService.log(
          'WARN',
          `[PagePreloadService] Compliance preload failed for page ${page.getPageNumber()}`,
          err
        )
      );
    this.planReviewerService
      .getPagePdf(page)
      .catch(err =>
        ClientLoggingService.log(
          'WARN',
          `[PagePreloadService] PDF preload failed for page ${page.getPageNumber()}`,
          err
        )
      );
  }

  /**
   * Find the immediate neighbors of `currentPage` in `allPages` and preload
   * them using idle callbacks so the work doesn't compete with user-visible
   * rendering.
   */
  preloadNeighborPages(currentPage: ArchitecturalPlanPage, allPages: ArchitecturalPlanPage[]): void {
    const currentIdx = allPages.findIndex(
      p =>
        p.getPageNumber() === currentPage.getPageNumber() && p.getPageFolderPath() === currentPage.getPageFolderPath()
    );
    if (currentIdx === -1) return;

    const neighbors: ArchitecturalPlanPage[] = [];
    for (let offset = 1; offset <= PagePreloadService.MAX_NEIGHBORS; offset++) {
      if (currentIdx - offset >= 0) {
        neighbors.push(allPages[currentIdx - offset]);
      }
      if (currentIdx + offset < allPages.length) {
        neighbors.push(allPages[currentIdx + offset]);
      }
    }

    if (neighbors.length === 0) return;

    const scheduleIdle =
      typeof requestIdleCallback !== 'undefined'
        ? (cb: IdleRequestCallback) => requestIdleCallback(cb, { timeout: 5000 })
        : (cb: () => void) => setTimeout(cb, 200);

    for (const neighbor of neighbors) {
      scheduleIdle(() => this.preloadPageData(neighbor));
    }
  }
}
